import { useEffect } from "react";
import { getToken, onMessage } from "firebase/messaging";
import { firebaseConfigured, getMessagingInstance, fcmServiceWorkerUrl } from "../firebase-config";
import { updateFcmToken } from "../api/authService";
import { useAuth } from "../contexts/AuthContext";
import { useRefresh } from "../contexts/RefreshContext";
import { showToast } from "../utils/toast";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let lastSentToken = null;

// Exposed so the logout flow can tell the backend "this device is done" and
// avoid leaving an orphan FCM token wired to the next user that signs in.
export const getCurrentFcmToken = () => lastSentToken;
export const clearCurrentFcmToken = () => { lastSentToken = null; };

const playSound = () => {
  try {
    const audio = new Audio("/notification-sound.wav");
    audio.volume = 0.7;
    audio.play().catch(() => {});
  } catch {
    /* sound init failed — non-fatal */
  }
};

export default function useFcmToken({ enabled = true } = {}) {
  const { triggerRefresh } = useRefresh();
  const { accessToken } = useAuth();

  useEffect(() => {
    if (!enabled) return;
    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      // 1) Ask for permission FIRST, regardless of whether Firebase Messaging
      //    or the page-level Firebase config is reachable. The browser API is
      //    independent of FCM and works on any HTTPS / localhost origin.
      if (typeof window === "undefined") return;
      if (!("Notification" in window)) {
        // eslint-disable-next-line no-console
        console.warn("[FCM] Browser has no Notification API.");
        return;
      }
      // Some browsers silently no-op on insecure origins.
      if (!window.isSecureContext) {
        // eslint-disable-next-line no-console
        console.warn("[FCM] Notifications require HTTPS or localhost. Skipping.");
        return;
      }

      let permission = Notification.permission;
      if (permission === "default") {
        try {
          permission = await Notification.requestPermission();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[FCM] requestPermission threw:", err);
          return;
        }
      }
      if (cancelled) return;
      if (permission !== "granted") {
        // eslint-disable-next-line no-console
        console.warn("[FCM] Notification permission =", permission);
        return;
      }

      // 2) Now wire up FCM if it's actually configured + supported.
      if (!firebaseConfigured) {
        // eslint-disable-next-line no-console
        console.warn("[FCM] Firebase env vars missing — skipping token registration.");
        return;
      }
      const messaging = await getMessagingInstance();
      if (cancelled || !messaging) return;

      try {
        // Register the worker ourselves with the config in the query string so
        // it can't drift from the page config. Registering the same scope with
        // a changed URL transparently updates an existing worker.
        const swReg = await navigator.serviceWorker.register(fcmServiceWorkerUrl(), { scope: "/" });
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: swReg,
        });
        if (!token) return;

        // Only POST the token if the user is signed in. Pre-login it would 401;
        // once accessToken changes the effect re-runs and we forward the cached
        // token to the backend.
        if (accessToken && token !== lastSentToken) {
          try {
            await updateFcmToken(token);
            lastSentToken = token;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[FCM] Failed to persist token", err);
          }
        }
      } catch (err) {
        // Make the cause obvious instead of a silent no-push. A 400 on the
        // Firebase Installations endpoint almost always means a bad apiKey/appId
        // in the build, or stale cached installation data after a config change.
        const code = err?.code || err?.message || String(err);
        // eslint-disable-next-line no-console
        console.error(
          `[FCM] Token registration failed (${code}). Check that REACT_APP_FIREBASE_* match the Firebase project, ` +
            "and clear site data if the project config recently changed.",
          err,
        );
      }

      // 3) Foreground messages — every payload produces a toast + sound + refresh.
      unsubscribe = onMessage(messaging, (payload) => {
        const data = payload?.data || {};
        triggerRefresh(data);
        playSound();
        const title = data.title || payload?.notification?.title || "Notification";
        const body = data.body || payload?.notification?.body || "";
        showToast("info", body ? `${title}: ${body}` : title);
      });
    })();

    return () => {
      cancelled = true;
      try { unsubscribe(); } catch { /* noop */ }
    };
  }, [enabled, triggerRefresh, accessToken]);
}
